import { useCallback, useState } from "react";
import {
  USDG, USDG_DEC, WETH, WETH_DEC, WETH_USDG_FEE, SWAP_ROUTER, MAX_UINT256, CHAIN_ID,
  readAllowance, sendApprove, sendSwap, sendSwapMultihop, encodePath, waitForReceipt, ensureChain,
} from "../lib/swap";
import { useToast } from "../components/Toast";

const EXPLORER = "https://robinhoodchain.blockscout.com/tx/";

/**
 * useSwap — real swap execution against Uniswap V3 SwapRouter02.
 *
 * BUY:  pay USDG or ETH  → receive stock token   (size = pay amount)
 * SELL: pay stock token  → receive USDG          (size = pay amount, token units)
 */
export default function useSwap({ wallet, refresh }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(null);

  const submit = useCallback(async ({
    side,
    token,
    tokenDec = 18,
    fee,
    poolPrice,   // USDG per stock token
    sizeStr,
    slippagePct = 0.5,
    ticker,
    payAsset = "USDG",  // 'USDG' | 'ETH' — only valid on BUY
  }) => {
    if (busy) return;
    if (!wallet?.rawAddress || wallet.chainId !== CHAIN_ID) {
      toast.error("Connect a wallet on Robinhood Chain 4663.");
      return;
    }
    if (!fee) { toast.error("No pool available for this token."); return; }
    const size = Number(sizeStr);
    if (!size || size <= 0) { toast.error("Enter a size."); return; }
    if (!poolPrice || poolPrice <= 0) { toast.error("Pool price unavailable."); return; }

    const from = wallet.rawAddress;
    const isBuy = side === "buy";
    const useEth = isBuy && payAsset === "ETH";

    try {
      setBusy(true);
      await ensureChain();

      if (useEth) {
        // ETH → WETH → USDG → token, single tx via SwapRouter02 multi-hop.
        // SwapRouter02 auto-wraps msg.value into WETH when the first path
        // token is WETH. No approval needed for native ETH.
        setStage("swap");
        const amountIn = BigInt(Math.floor(Number(sizeStr) * 10 ** WETH_DEC));
        // We don't have a WETH/USDG price on the client, so we take a
        // conservative min: (usdg_notional / poolPrice) * (1-slip)
        // where usdg_notional is estimated by a small on-chain quote is
        // ideal, but for MVP we require the user to accept larger slippage
        // when using ETH (default 3% recommended).
        // Estimate: expect ~1 ETH ≈ 3700 USDG (typical), tokens = 3700 / poolPrice.
        // Better UX: bump slippage tolerance for ETH-in.
        const slip = Math.max(slippagePct, 1.0);
        // We can't know exact ETH→USDG without a quote — use a floor of 0 to
        // allow the swap to fill at whatever the pool gives (safe for tiny
        // sizes but risky for large trades). For safety, require user set
        // amountOutMin=0 only when size ≤ 1 ETH; else abort.
        if (Number(sizeStr) > 1.0) {
          toast.error("ETH-in supports up to 1 ETH per tx while we quote via router. Split, or pay USDG.");
          setBusy(false); setStage(null); return;
        }
        const path = encodePath([WETH, USDG, token], [WETH_USDG_FEE, fee]);
        const amountOutMin = 0n; // small size — accept market
        toast.info(`Buying ${ticker} with ETH…`);
        const swapTx = await sendSwapMultihop({
          from, path, amountIn, amountOutMin, valueEth: amountIn,
        });
        toast.info("Swap sent", { link: EXPLORER + swapTx });
        setStage("confirm");
        const rc = await waitForReceipt(swapTx);
        if (rc?.status === "0x1") {
          toast.success(`Bought ${ticker}`, { link: EXPLORER + swapTx });
          if (refresh) refresh();
        } else {
          toast.error("Swap reverted", { link: EXPLORER + swapTx });
        }
        return;
      }

      // Single-hop path (USDG↔token)
      const tokenIn  = isBuy ? USDG : token;
      const tokenOut = isBuy ? token : USDG;
      const decIn    = isBuy ? USDG_DEC : tokenDec;
      const decOut   = isBuy ? tokenDec : USDG_DEC;

      const size = Number(sizeStr);
      const amountIn = BigInt(Math.floor(size * 10 ** decIn));
      const outFloat = isBuy ? (size / poolPrice) : (size * poolPrice);
      const amountOutMin = BigInt(Math.floor(outFloat * (1 - slippagePct / 100) * 10 ** decOut));

      setStage("approve");
      const cur = await readAllowance(tokenIn, from, SWAP_ROUTER);
      if (cur < amountIn) {
        toast.info("Approve token in wallet…");
        const apTx = await sendApprove(tokenIn, SWAP_ROUTER, from, MAX_UINT256);
        toast.info("Approval sent", { link: EXPLORER + apTx });
        await waitForReceipt(apTx);
        toast.success("Approval confirmed");
      }

      setStage("swap");
      toast.info(`${isBuy ? "Buying" : "Selling"} ${ticker}…`);
      const swapTx = await sendSwap({
        from, tokenIn, tokenOut, fee, amountIn, amountOutMin,
      });
      toast.info("Swap sent", { link: EXPLORER + swapTx });

      setStage("confirm");
      const rc = await waitForReceipt(swapTx);
      const ok = rc?.status === "0x1";
      if (ok) {
        toast.success(`${isBuy ? "Bought" : "Sold"} ${ticker}`, { link: EXPLORER + swapTx });
        if (refresh) refresh();
      } else {
        toast.error("Swap reverted", { link: EXPLORER + swapTx });
      }
    } catch (e) {
      const msg = e?.message || "Swap failed";
      toast.error(msg.length > 80 ? msg.slice(0, 80) + "…" : msg);
    } finally {
      setBusy(false);
      setStage(null);
    }
  }, [busy, wallet, toast, refresh]);

  return { submit, busy, stage };
}
